import { FC } from 'react';
import { withStyles, Card, formattedCurrency, ClientAccountBalance, Tooltip } from 'vopay';
import styles from './BalancesCard.module.scss';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

type BalanceCardProps = {
  balances: ClientAccountBalance[];
  country: string;
};

const BalancesCard: FC<BalanceCardProps> = ({ balances, country }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccount.BalancesCard' });
  const defaultCurrency = country === 'US' ? 'USD' : 'CAD';

  return (
    <Card
      style={{
        padding: '24px 44px 32px',
      }}
    >
      <div className={cn('balancesTitle')}>{t('availableBalance', { defaultValue: 'Available Balance' })}</div>

      <div className={cn('balances')}>
        {balances.length ? (
          balances.map((balance) => (
            <div key={balance.Currency || defaultCurrency} className={cn('currency')}>
              <span>{balance.Currency || defaultCurrency}</span>
              <span>{formattedCurrency(balance.AvailableBalance?.toString() || '0')}</span>
              <CurrencyTooltip balance={balance} defaultCurrency={defaultCurrency} />
            </div>
          ))
        ) : (
          <div key={defaultCurrency} className={cn('currency')}>
            <span>{defaultCurrency}</span>
            <span>{formattedCurrency('0')}</span>
          </div>
        )}
      </div>
    </Card>
  );
};

export default BalancesCard;

function CurrencyTooltip({ balance, defaultCurrency }: { balance: ClientAccountBalance; defaultCurrency: string }) {
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccount.BalancesCard' });
  const currency = balance.Currency || defaultCurrency;

  return (
    <Tooltip
      icon="info"
      position="right"
      information={
        <div className={cn('currencyTooltip')}>
          <div className={cn('tooltipSection')}>
            <div>{t('total', { defaultValue: 'Total' })}</div>
            <div>
              {currency} {formattedCurrency(balance.Balance.toString())}
            </div>
          </div>

          <div className={cn('tooltipSection')}>
            <div>{t('pending', { defaultValue: 'Pending' })}</div>
            <div>
              {currency} {formattedCurrency(balance.PendingBalance.toString())}
            </div>
          </div>

          <div className={cn('tooltipSection')}>
            <div>{t('available', { defaultValue: 'Available' })}</div>
            <div>
              {currency} {formattedCurrency(balance.AvailableBalance.toString())}
            </div>
          </div>
        </div>
      }
    />
  );
}
